import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, KeyRound, Loader2, Pause, Plus, Trash2, Users, X } from 'lucide-react';
import { useHeaderAction } from '@/lib/header-action-context';
import { usePageTitle } from '@/hooks/use-page-title';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import {
  useCreateFederationPeer,
  useFederationPeers,
  usePauseFederationPeer,
  useRevokeFederationPeer,
  useRotateFederationPeerSecret,
  type FederationPeer,
  type FederationPeerSecretReveal,
} from '@/hooks/use-federation-peers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

const createPeerButtonClass =
  'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function FederationPeersPage() {
  usePageTitle('Federation Peers');
  const { setHeaderAction, setHeaderTitle } = useHeaderAction();

  const [createOpen, setCreateOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [remoteAlias, setRemoteAlias] = useState('');
  const [remoteIssuerUrl, setRemoteIssuerUrl] = useState('');
  const [mirrorCapabilityIds, setMirrorCapabilityIds] = useState('');
  const [createdSecret, setCreatedSecret] = useState<FederationPeerSecretReveal | null>(null);
  const [rotatedSecret, setRotatedSecret] = useState<FederationPeerSecretReveal | null>(null);
  const [pending, setPending] = useState<{ kind: 'rotate' | 'pause' | 'revoke'; peer: FederationPeer } | null>(null);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading, isError, refetch } = useFederationPeers();
  const createMutation = useCreateFederationPeer();
  const rotateMutation = useRotateFederationPeerSecret();
  const pauseMutation = usePauseFederationPeer();
  const revokeMutation = useRevokeFederationPeer();
  const peers = data?.peers ?? [];

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredPeers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    let result = peers.filter((peer) => {
      const matchesSearch =
        !q ||
        peer.display_name.toLowerCase().includes(q) ||
        peer.remote_registry_alias.toLowerCase().includes(q) ||
        peer.remote_issuer_url.toLowerCase().includes(q) ||
        peer.status.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all' || peer.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    return [...result].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'status') {
        cmp = a.status.localeCompare(b.status);
      } else if (sortField === 'created') {
        cmp = a.created.localeCompare(b.created);
      } else {
        cmp = a.display_name.localeCompare(b.display_name);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [peers, searchQuery, statusFilter, sortField, sortDirection]);

  useEffect(() => {
    setHeaderTitle({
      icon: (
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-100 text-indigo-700">
          <Users size={16} />
        </div>
      ),
      title: 'Federation Peers',
      subtitle: 'Manage connected workspaces and cross-registry federation.',
    });
    setHeaderAction(
      <button type="button" onClick={() => setCreateOpen(true)} className={createPeerButtonClass}>
        <Plus size={14} />
        Add Federation Peer
      </button>,
    );
    return () => {
      setHeaderTitle(null);
      setHeaderAction(null);
    };
  }, [setHeaderAction, setHeaderTitle]);

  const resetCreateForm = () => {
    setDisplayName('');
    setRemoteAlias('');
    setRemoteIssuerUrl('');
    setMirrorCapabilityIds('');
  };

  const copyValue = async (value: string, which: 'id' | 'secret') => {
    try {
      await navigator.clipboard.writeText(value);
      if (which === 'id') {
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 1500);
      } else {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 1500);
      }
    } catch {
      // ignore clipboard errors
    }
  };

  const submitCreate = async () => {
    const mirror = mirrorCapabilityIds
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const result = await createMutation.mutateAsync({
      display_name: displayName.trim(),
      remote_registry_alias: remoteAlias.trim(),
      remote_issuer_url: remoteIssuerUrl.trim(),
      mirror_capability_ids: mirror,
    });
    setCreatedSecret(result);
    setCreateOpen(false);
    resetCreateForm();
    refetch();
  };

  const runPending = async () => {
    if (!pending) return;
    if (pending.kind === 'rotate') {
      const secret = await rotateMutation.mutateAsync(pending.peer.id);
      setRotatedSecret(secret);
    } else if (pending.kind === 'pause') {
      await pauseMutation.mutateAsync(pending.peer.id);
    } else {
      await revokeMutation.mutateAsync(pending.peer.id);
    }
    setPending(null);
    refetch();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {isError ? (
        <Card className="p-8 text-center text-sm text-red-600">Unable to load federation peers.</Card>
      ) : (
        <CapabilityListPanel<FederationPeer>
          items={filteredPeers}
          sortOptions={[
            { key: 'name', label: 'Name' },
            { key: 'status', label: 'Status' },
            { key: 'created', label: 'Created' },
          ]}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search federation peers..."
          allTags={[]}
          selectedTags={new Set<string>()}
          onToggleTag={() => {}}
          onClearAllTags={() => {}}
          onSelectAllTags={() => {}}
          showTagsFilter={false}
          extraToolbar={
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={cn('w-40', listControlsGlassTrigger)}>
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
              </SelectContent>
            </Select>
          }
          isLoading={isLoading}
          loadingMessage="Loading federation peers..."
          emptyStateVariant="glass"
          emptyMessage="No federation peers configured."
          gridClassName="grid grid-cols-1 gap-3"
          renderCard={(peer) => (
            <Card key={peer.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{peer.display_name}</h3>
                <p className="text-xs text-slate-500">
                  Remote workspace: <span className="font-medium">{peer.remote_registry_alias}</span>
                </p>
                <p className="text-xs text-slate-500">Issuer: {peer.remote_issuer_url}</p>
                <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <div>
                    <dt className="inline text-slate-400">Status: </dt>
                    <dd className="inline">{peer.status}</dd>
                  </div>
                  <div>
                    <dt className="inline text-slate-400">Last handshake: </dt>
                    <dd className="inline">{formatDate(peer.last_handshake_at)}</dd>
                  </div>
                </dl>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPending({ kind: 'rotate', peer })}>
                  <KeyRound className="mr-1.5 h-4 w-4" />
                  Rotate
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPending({ kind: 'pause', peer })}>
                  <Pause className="mr-1.5 h-4 w-4" />
                  Pause
                </Button>
                <Button variant="outline" size="sm" className="text-destructive" onClick={() => setPending({ kind: 'revoke', peer })}>
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Revoke
                </Button>
              </div>
            </Card>
          )}
        />
      )}

      <SlideDrawer isOpen={createOpen} onClose={() => setCreateOpen(false)}>
          <div className="relative border-b border-slate-100 bg-slate-50/50 px-8 py-6">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                  <Users className="h-5 w-5 text-indigo-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">Add Federation Peer</h2>
                  <p className="text-sm text-slate-500">Create credentials for a connected workspace.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto bg-white p-8">
            <div className="mx-auto w-full max-w-3xl space-y-6">
              <div className="space-y-2">
              <Label htmlFor="peer-name">Display name</Label>
              <Input id="peer-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="peer-alias">Remote workspace alias</Label>
                <Input id="peer-alias" value={remoteAlias} onChange={(e) => setRemoteAlias(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="peer-issuer">Remote issuer URL</Label>
                <Input id="peer-issuer" value={remoteIssuerUrl} onChange={(e) => setRemoteIssuerUrl(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="peer-mirror">Mirror capability IDs (comma separated)</Label>
                <Input id="peer-mirror" value={mirrorCapabilityIds} onChange={(e) => setMirrorCapabilityIds(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-8 py-6">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void submitCreate()}
              disabled={createMutation.isPending || !displayName.trim() || !remoteAlias.trim() || !remoteIssuerUrl.trim()}
            >
              {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </div>
      </SlideDrawer>

      <Dialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.kind === 'rotate'
                ? `Rotate secret for ${pending.peer.display_name}?`
                : pending?.kind === 'pause'
                  ? `Pause ${pending.peer.display_name}?`
                  : pending?.kind === 'revoke'
                    ? `Revoke ${pending.peer.display_name}?`
                    : ''}
            </DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button onClick={() => void runPending()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!createdSecret} onOpenChange={(open) => !open && setCreatedSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Credentials created</DialogTitle>
            <DialogDescription>Save these now. The secret is shown only once.</DialogDescription>
          </DialogHeader>
          {createdSecret ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Peer ID</Label>
                <div className="flex gap-2">
                  <Input readOnly value={createdSecret.client_id} />
                  <Button variant="outline" size="icon" onClick={() => void copyValue(createdSecret.client_id, 'id')}>
                    {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Peer Secret</Label>
                <div className="flex gap-2">
                  <Input readOnly value={createdSecret.client_secret} />
                  <Button variant="outline" size="icon" onClick={() => void copyValue(createdSecret.client_secret, 'secret')}>
                    {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rotatedSecret} onOpenChange={(open) => !open && setRotatedSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Secret rotated</DialogTitle>
            <DialogDescription>The previous secret was invalidated. Save this replacement now.</DialogDescription>
          </DialogHeader>
          {rotatedSecret ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Peer ID</Label>
                <Input readOnly value={rotatedSecret.client_id} />
              </div>
              <div className="space-y-1">
                <Label>Peer Secret</Label>
                <Input readOnly value={rotatedSecret.client_secret} />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
